import React from 'react';
import { motion } from 'framer-motion';
import { hero, marquee } from '../../data/portfolioData';

const fadeIn = {
  hidden: { opacity: 0, y: 34 },
  visible: { opacity: 1, y: 0 },
};

function scrollToSection(id) {
  const section = document.getElementById(id);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

export default function HeroSection() {
  return (
    <section id="hero" className="relative mx-auto flex w-full max-w-7xl flex-col px-4 pb-20 pt-32 sm:px-6 lg:px-10 lg:pt-40">
      <div className="grid items-center gap-8 lg:grid-cols-[1.15fr_0.85fr]">
        <motion.div
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="space-y-6"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.26em] text-cyan-300">Masterpiece Portfolio</p>
          <h1 className="font-display text-4xl font-bold leading-[0.95] text-white sm:text-6xl lg:text-8xl">
            {hero.name}
          </h1>
          <p className="text-lg font-semibold text-slate-100 sm:text-2xl">{hero.role}</p>
          <p className="max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">{hero.tagline}</p>
          <p className="max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{hero.description}</p>

          <div className="flex flex-wrap gap-4">
            <button
              type="button"
              data-magnetic
              onClick={() => scrollToSection('projects')}
              className="rounded-full bg-neon px-7 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white shadow-glow"
            >
              View Projects
            </button>
            <button
              type="button"
              data-magnetic
              onClick={() => scrollToSection('contact')}
              className="rounded-full border border-white/30 bg-white/5 px-7 py-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-100 backdrop-blur"
            >
              Start a Project
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {hero.stats.map((item, index) => (
              <motion.article
                key={item.label}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.12, duration: 0.5 }}
                className="glass-card"
              >
                <p className="text-xs uppercase tracking-[0.15em] text-slate-400">{item.label}</p>
                <h3 className="mt-2 font-display text-lg font-bold text-white">{item.value}</h3>
              </motion.article>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, rotateX: 10, rotateY: -8 }}
          animate={{ opacity: 1, scale: 1, rotateX: 0, rotateY: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="relative perspective-1000"
        >
          <div className="glass-card relative overflow-hidden p-5 sm:p-8">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(48,166,255,0.35),transparent_50%),radial-gradient(circle_at_bottom_left,rgba(122,92,255,0.3),transparent_50%)]" />
            <img
              src={hero.image}
              alt="Developer workspace illustration"
              className="relative z-10 mx-auto w-full max-w-md animate-float"
              loading="eager"
            />
          </div>
        </motion.div>
      </div>

      <div className="marquee mt-16">
        <div className="marquee-track">
          {marquee.map((item, index) => (
            <span key={`${item}-${index}`} className="marquee-item">
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
